import { useState } from 'react';

// Enum values and storage keys, not user-facing copy.
// eslint-disable-next-line i18next/no-literal-string
export const VIEW_MODE = { cards: 'cards', list: 'list' } as const;

export type ViewMode = (typeof VIEW_MODE)[keyof typeof VIEW_MODE];

const isViewMode = (value: string | null): value is ViewMode =>
  value === VIEW_MODE.cards || value === VIEW_MODE.list;

const read = (storageKey: string, defaultMode: ViewMode): ViewMode => {
  try {
    const raw = localStorage.getItem(storageKey);
    return isViewMode(raw) ? raw : defaultMode;
  } catch {
    return defaultMode;
  }
};

/**
 * Which of the two ways a list surface is drawn, remembered per browser.
 *
 * Persisted rather than kept for the visit: the view someone works in is a standing preference,
 * and a page that reset on every arrival would be re-chosen every time. Anything unrecognised in
 * storage falls back to the default rather than being trusted, so a stale or hand-edited value
 * cannot leave the page with nothing to render.
 */
export function useStoredViewMode(storageKey: string, defaultMode: ViewMode) {
  const [viewMode, setStored] = useState<ViewMode>(() => read(storageKey, defaultMode));

  const setViewMode = (next: ViewMode) => {
    setStored(next);
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      /* storage blocked, or the quota is full -- a forgotten preference is not worth an error */
    }
  };

  return { viewMode, setViewMode };
}
